import { useEffect, useState } from 'react';
import styles from './FilmGrain.module.css';

const TILE = 512;
const GRAIN = 5;
const BRIGHT_CHANCE = 0.11;
const DARK_CHANCE = 0.09;
const BASE_OPACITY = 0.6;

export const GRAIN_INTENSITY = {
  full: 1,
  subtle: 0.2,
  heroBoost: 0.8,
  whisper: 0.1,
};

let textureUrl = '';
const listeners = new Set();
let intervalId = null;

function paintGrain(ctx) {
  const imageData = ctx.createImageData(TILE, TILE);
  const { data } = imageData;

  for (let y = 0; y < TILE; y += GRAIN) {
    for (let x = 0; x < TILE; x += GRAIN) {
      const roll = Math.random();

      let value = null;
      let alpha = 0;

      if (roll < BRIGHT_CHANCE) {
        value = 255;
        alpha = 70 + Math.random() * 90;
      } else if (roll < BRIGHT_CHANCE + DARK_CHANCE) {
        value = 0;
        alpha = 60 + Math.random() * 80;
      }

      if (value === null) continue;

      for (let dy = 0; dy < GRAIN; dy += 1) {
        for (let dx = 0; dx < GRAIN; dx += 1) {
          const px = x + dx;
          const py = y + dy;
          if (px >= TILE || py >= TILE) continue;

          const i = (py * TILE + px) * 4;
          data[i] = value;
          data[i + 1] = value;
          data[i + 2] = value;
          data[i + 3] = alpha;
        }
      }
    }
  }

  ctx.putImageData(imageData, 0, 0);
}

function refreshTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = TILE;
  canvas.height = TILE;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  paintGrain(ctx);
  textureUrl = canvas.toDataURL('image/png');
  listeners.forEach((notify) => notify());
}

function ensureTextureLoop() {
  if (intervalId !== null || listeners.size === 0) return;

  refreshTexture();

  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (!media.matches) {
    intervalId = window.setInterval(refreshTexture, 80);
  }
}

function stopTextureLoopIfIdle() {
  if (listeners.size > 0 || intervalId === null) return;

  window.clearInterval(intervalId);
  intervalId = null;
}

function useGrainTexture() {
  const [, setVersion] = useState(0);

  useEffect(() => {
    const notify = () => setVersion((value) => value + 1);

    listeners.add(notify);
    ensureTextureLoop();

    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onMotionChange = () => {
      if (intervalId) {
        window.clearInterval(intervalId);
        intervalId = null;
      }

      refreshTexture();

      if (!media.matches && listeners.size > 0) {
        intervalId = window.setInterval(refreshTexture, 80);
      }
    };

    media.addEventListener('change', onMotionChange);

    return () => {
      listeners.delete(notify);
      media.removeEventListener('change', onMotionChange);
      stopTextureLoopIfIdle();
    };
  }, []);

  return textureUrl;
}

function useSectionBounds(sectionRef) {
  const [bounds, setBounds] = useState(null);

  useEffect(() => {
    const section = sectionRef?.current;
    if (!section) return undefined;

    const update = () => {
      const rect = section.getBoundingClientRect();
      setBounds({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    };

    update();

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    const observer = new ResizeObserver(update);
    observer.observe(section);

    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      observer.disconnect();
    };
  }, [sectionRef]);

  return bounds;
}

function grainStyle(texture, intensity) {
  return {
    backgroundImage: texture ? `url(${texture})` : undefined,
    opacity: intensity * BASE_OPACITY,
  };
}

export default function FilmGrain({ intensity = GRAIN_INTENSITY.full, sectionRef = null }) {
  const texture = useGrainTexture();
  const bounds = useSectionBounds(sectionRef);

  if (sectionRef) {
    if (!bounds) return null;

    return (
      <div
        className={`${styles.layerBoost} film-grain-layer`}
        style={{
          ...grainStyle(texture, intensity),
          top: `${bounds.top}px`,
          left: `${bounds.left}px`,
          width: `${bounds.width}px`,
          height: `${bounds.height}px`,
        }}
        aria-hidden="true"
      />
    );
  }

  return (
    <div
      className={`${styles.layerGlobal} film-grain-layer`}
      style={grainStyle(texture, intensity)}
      aria-hidden="true"
    />
  );
}
